import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../api/api';
import { KPI_COLUMNS, defaultDateRange } from '../utils/vendorKpi';
import { DateFilters, KpiValueCell, WeightRow, StatusIcon } from '../components/vendorMgmt/VendorKpiUi';

export default function VendorPerformance() {
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const defaults = defaultDateRange();
  const [startDate, setStartDate] = useState(defaults.start_date);
  const [endDate, setEndDate] = useState(defaults.end_date);
  const [kpiGroup, setKpiGroup] = useState('All');
  const [kpiGroups, setKpiGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('vendor_score');
  const [dir, setDir] = useState('asc');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        kpi_group: kpiGroup,
        page: String(page),
        limit: String(limit),
        sort,
        dir,
      });
      const data = await api.get(`/vendors/performance?${qs}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setKpiGroups(data.kpi_groups || []);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, kpiGroup, page, limit, sort, dir]);

  useEffect(() => {
    dispatch({ type: 'SET_BREADCRUMB', payload: ['Home', 'Vendor Management', 'Vendor performance'] });
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    if (sort === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir('asc'); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-lg font-bold text-sap-text">Vendor performance</h1>
        <DateFilters
          startDate={startDate}
          endDate={endDate}
          onStartChange={(v) => { setStartDate(v); setPage(1); }}
          onEndChange={(v) => { setEndDate(v); setPage(1); }}
          kpiGroup={kpiGroup}
          kpiGroups={kpiGroups}
          onKpiGroupChange={(v) => { setKpiGroup(v); setPage(1); }}
        />
      </div>

      <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="bg-white border-b border-sap-border/60 text-left text-[11px] uppercase tracking-wide text-sap-textLight">
                <th className="px-4 py-3 font-semibold">Vendor</th>
                <th className="px-4 py-3 font-semibold text-right">
                  <button type="button" onClick={() => toggleSort('vendor_score')} className="inline-flex items-center gap-1 hover:text-sap-text">
                    Vendor Score {sort === 'vendor_score' && (dir === 'asc' ? '↑' : '↓')}
                  </button>
                </th>
                {KPI_COLUMNS.map((col) => (
                  <th key={col.key} className="px-4 py-3 font-semibold text-right">
                    <button type="button" onClick={() => toggleSort(col.key)} className="inline-flex items-center gap-1 hover:text-sap-text">
                      {col.label} {sort === col.key && (dir === 'asc' ? '↑' : '↓')}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <WeightRow columns={KPI_COLUMNS} />
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sap-textLight text-xs">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sap-textLight text-xs">No vendors match filters</td></tr>
              ) : (
                rows.map((row) => {
  console.log("Performance Row:", row);

  return (
    <tr
      key={row.vendor_id}
      className="border-t border-sap-border/40 hover:bg-sap-blue/5 cursor-pointer"
      onClick={() => {
        console.log("Clicked Vendor ID:", row.vendor_id);
        navigate(`/vendor-management/vendor/${row.vendor_id}`);
      }}
    >
                    <td className="px-4 py-2.5 font-medium text-sap-blue">{row.vendor_name}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center gap-1.5 justify-end">
                        <StatusIcon value={row.vendor_score} />
                        <span className="font-semibold">{Number(row.vendor_score).toFixed(2)}%</span>
                      </div>
                    </td>
                    {KPI_COLUMNS.map((col) => (
                      <td key={col.key} className="px-4 py-2.5 text-right">
                        <KpiValueCell value={row[col.key]} isPct={col.isPct} />
                      </td>
                    ))}
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-sap-border/60 flex flex-wrap items-center justify-between gap-3 text-xs text-sap-textLight">
          <span>Showing {from}-{to} of {total}</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">‹</button>
            <span className="px-2">{page}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">›</button>
          </div>
          <label className="flex items-center gap-2">
            Rows per page
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1 bg-white">
              {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}
